import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols";
import { type FC, useRef } from "react";
import type { AriaSliderProps } from "react-aria";
import { useNumberFormatter, useSlider, VisuallyHidden } from "react-aria";
import { useSliderState } from "react-stately";

import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TriggerButtonIntent } from "../TriggerButton/settings.ts";
import { TriggerButton, type TriggerButtonProps } from "../TriggerButton/TriggerButton.tsx";
import style from "./Slider.module.css";
import { Thumb } from "./Thumb.tsx";

export interface SliderProps extends AriaSliderProps {
  icon?: MaterialSymbol;
  isUiSmall?: boolean;
  noHeader?: boolean;
  tooltip?: boolean | ((value: number) => void);
  formatOptions?: Parameters<typeof useNumberFormatter>[0];
  controls?: {
    left?: Omit<TriggerButtonProps, "intent" | "size">;
    right?: Omit<TriggerButtonProps, "intent" | "size">;
  };
}

/**
 * A Slider component able to manage as many thumbs as need.
 * A single value or multiple values are always represented in an array.
 * ! Except for the tooltip because is representing a single value.
 */
export const Slider: FC<SliderProps> = ({ icon, isUiSmall, formatOptions, ...props }) => {
  const trackRef = useRef(null);
  const numberFormatter = useNumberFormatter(formatOptions);
  const state = useSliderState({ ...props, numberFormatter });
  const { groupProps, trackProps, labelProps, outputProps } = useSlider(props, state, trackRef);

  return (
    <VStack
      spacing={2}
      className={clsx(style.container, {
        [style.with_icon]: (icon || props.controls) && !isUiSmall,
        [style.disabled]: props.isDisabled,
      })}
      data-testid={"slider-widget"}
    >
      {!props.noHeader && (
        <HStack domProps={groupProps}>
          {props.label != undefined && <Label {...labelProps}>{props.label}</Label>}
          <Spacer />
          <output {...outputProps} className={style.label}>
            {state.values.map((_, idx) => state.getThumbValueLabel(idx)).join(" – ")}
          </output>
        </HStack>
      )}
      <HStack spacing={3} className={style.slider}>
        {!!props.label && !!props.noHeader && (
          <VisuallyHidden>
            <label {...labelProps}>{props.label}</label>
          </VisuallyHidden>
        )}
        {!!icon && !isUiSmall && !props.controls && <SymbolIcon icon={icon} className={style.icon} />}
        {!!props.controls?.left && (
          <TriggerButton
            {...props.controls.left}
            intent={TriggerButtonIntent.Secondary}
            className={style.control_left}
            isDisabled={props.controls.left.isDisabled ?? props.isDisabled}
          />
        )}
        <div {...trackProps} ref={trackRef} className={style.track}>
          <div className={style.track_bar}>
            {state.values.map((_, idx) => (
              <Thumb
                index={idx}
                state={state}
                trackRef={trackRef}
                isDisabled={props.isDisabled}
                tooltip={props.tooltip}
                key={`thumb-${idx}`}
              />
            ))}
          </div>
        </div>
        {!!props.controls?.right && (
          <TriggerButton
            {...props.controls.right}
            intent={TriggerButtonIntent.Secondary}
            className={style.control_right}
            isDisabled={props.controls.right.isDisabled ?? props.isDisabled}
          />
        )}
      </HStack>
    </VStack>
  );
};
